<script lang="ts" setup>
import cloneDeep from 'lodash/cloneDeep'

const props = defineProps({
  allowLastConditionDeletion: {
    default: true,
    type: Boolean,
  },
  attributesToExclude: {
    default: () => [],
    type: Array as PropType<SegmentationAttribute[]>,
  },
  companies: {
    default: undefined,
    type: Array as PropType<CompanyV2[]>,
  },
  hasActions: {
    default: true,
    type: Boolean,
  },
  isPreview: {
    default: false,
    type: Boolean,
  },
  maxConditions: {
    default: Number.POSITIVE_INFINITY,
    type: Number,
  },
  modelValue: {
    required: true,
    type: Array as PropType<SegmentationCondition[]>,
  },
  updateAiOnType: {
    default: false,
    type: Boolean,
  },
})

const emit = defineEmits<{
  'conditionAdded': []
  'conditionConfirmed': [condition: SegmentationCondition, conditionIndex: number]
  'conditionRemoved': [conditionIndex: number]
  'conditionUpdated': [updates: SegmentationCondition, conditionIndex: number]
  'update:modelValue': [value: SegmentationCondition[]]
}>()

const { t } = useI18n()

function onConditionAdded() {
  emit('conditionAdded')
}

function onConditionRemoved(conditionIndex: number) {
  emit('conditionRemoved', conditionIndex)
}

function onConditionConfirmed(condition: SegmentationCondition, conditionIndex: number) {
  emit('conditionConfirmed', condition, conditionIndex)
}

function onConditionUpdated(condition: SegmentationCondition, conditionIndex: number) {
  const conditions = cloneDeep(props.modelValue)

  conditions[conditionIndex] = condition

  emit('update:modelValue', conditions)
  emit('conditionUpdated', condition, conditionIndex)
}
</script>

<template>
  <div class="flex flex-col text-gray-900">
    <div
      v-for="(condition, conditionIndex) in modelValue"
      :key="conditionIndex"
      :class="{
        'mb-5': isPreview && conditionIndex + 1 !== modelValue.length,
        'mb-6': !isPreview && conditionIndex + 1 !== modelValue.length,
      }"
    >
      <SegmentationCondition
        :allow-last-condition-deletion="allowLastConditionDeletion"
        :attribute="condition.attribute"
        :attributes-to-exclude="attributesToExclude"
        :blocked-operator="condition.blockedOperator"
        :companies="companies"
        :condition-index="conditionIndex"
        :index="conditionIndex"
        :is-preview="isPreview"
        :operator="condition.operator"
        :sign="condition.sign"
        :update-ai-on-type="updateAiOnType"
        :values="condition.values"
        @condition-confirmed="(condition) => onConditionConfirmed(condition, conditionIndex)"
        @condition-removed="onConditionRemoved"
        @condition-updated="onConditionUpdated"
      />
    </div>

    <div
      v-if="hasActions && !isPreview"
      class="flex flex-col gap-2 md:flex-row"
      :class="{ 'mt-4': modelValue.length > 0 }"
    >
      <SegmentationAction
        v-if="modelValue.length < maxConditions"
        :icon="getIcon('filter')"
        icon-color="text-gray-500"
        :label="t('globals.addCondition')"
        @click="onConditionAdded"
      />

      <slot name="actions" />
    </div>

    <slot name="footer" />
  </div>
</template>
